import { Button, Form, Input, Upload, message } from "antd";
import React, { useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { db, storage } from "../../config/firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { addDoc, collection } from "firebase/firestore";
import { useMyContext } from "../../context/MyContext";
import { toast } from "react-toastify";
// getNextInquireNumber removed in favor of server-side allocation
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../config/api";

const UploadInvoice = () => {
  const { user } = useMyContext();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const handleSubmit = async (values) => {
    if (values.file?.length > 0) {
      try {
        setLoading(true);
        const downloadURLs = await Promise.all(
          values.file.map(async (file) => {
            const storageRef = ref(storage, `invoiceFiles/${file.name}`);
            await uploadBytes(storageRef, file.originFileObj);
            return getDownloadURL(storageRef);
          })
        );

        // Allocate a unique inquire number from backend
        let nextNumber = "";
        try {
          const allocRes = await api.post("/firestore/inquire-number/allocate");
          nextNumber = allocRes?.data?.inquireNumber || "";
          if (!nextNumber) {
            throw new Error("Server did not return an inquire number");
          }
        } catch (err) {
          const serverMsg = err.response?.data?.error || err.response?.data?.details || err.message;
          const isAuth = err.response?.status === 401;
          console.error("Failed to allocate inquire number", err.response?.data || err);
          if (isAuth) {
            toast.error("Session expired or not authorized. Please sign in again.");
          } else {
            toast.error(serverMsg || "Failed to allocate inquire number. Please try again.");
          }
          setLoading(false);
          return;
        }

        await saveFileData(values, downloadURLs, nextNumber);

        // Send notification to admin
        await api.post("/notifications", {
          title: "New Inquiry Request",
          message: `A new Inquiry request has been submitted by ${user?.name}`,
          linkText: "View Request",
          link: `/newRequest`,
          role: "admin",
          quoteNumber: nextNumber,
          type: "new-inquiry"
        });

        // Invalidate and refetch the notifications query
        queryClient.invalidateQueries(["notifications"]);

        setLoading(false);
        form.resetFields();
        toast.success("Invoices uploaded and admin notified successfully!");
      } catch (error) {
        console.error("Error uploading files:", error);
        toast.error("Error uploading files and notifying admin.");
        setLoading(false); // Ensure loading state is reset in case of error
      }
    } else {
        toast.error("Please select at least one file");
      }
    };

  const saveFileData = async (values, downloadURLs, nextNumber) => {
    try {
      const { file, ...others } = values;
      let fileData = file.map((a, index) => ({
        name: a.name,
        type: a.type,
        size: a.size,
        uid: a.uid,
        url: downloadURLs[index],
        thumbUrl: downloadURLs[index],
      }));

      const filesCollection = collection(db, "requests");
      await addDoc(filesCollection, {
        uploadFileData: fileData,
        isInvoiceUpload: true,
        userId: user?.uid,
        inquireNumber: nextNumber,
        name: user?.name,
        requestStatus: "Unquoted",
        creationDate: new Date().toISOString(),
        ...others,
      });

      toast.success("File data saved successfully.");
    } catch (error) {
      toast.error("Error saving file data:", error);
      console.error("Error saving file data:", error);
    }
  };

  const beforeUpload = (file) => {
    const isFileTypeValid =
      file.type === "application/pdf" ||
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.type === "text/csv" ||
      file.name.toLowerCase().endsWith(".csv");
    if (!isFileTypeValid) {
      toast.error("You can only upload PDF, Excel, or CSV files!");
    }
    return false;
  };

  const validateFileType = (_, fileList) => {
    if (fileList && fileList.length > 0) {
      const invalidFiles = fileList.filter(file => !(
        file.type === "application/pdf" ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.type === "text/csv" ||
        file.name.toLowerCase().endsWith(".csv")
      ));
      if (invalidFiles.length > 0) {
        return Promise.reject("You can only upload PDF, Excel, or CSV files!");
      }
    }
    return Promise.resolve();
  };

  return (
    <div style={{ margin: 30 }}>
      <Form layout="vertical" onFinish={handleSubmit} form={form}>
        <Form.Item
          label="Invoice Files"
          name="file"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          rules={[
            { required: true, message: "Please Select at least one File" },
            { validator: validateFileType },
          ]}
        >
          <Upload.Dragger
            beforeUpload={beforeUpload}
            name="files"
            multiple={true}
            accept=".pdf,.xlsx,.xls,.csv"
            customRequest={async ({ file, onSuccess, onError }) => {
              // Handle custom request if needed
              try {
                // Fake upload implementation for demo purposes
                await new Promise((resolve) => setTimeout(resolve, 1000));
                onSuccess();
              } catch (error) {
                onError(error);
              }
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Supporting Documents (E.G. Commercial Invoice, Packing List, Datasheets)
            </p>
            <p className="ant-upload-hint">
              Only PDF, Excel and CSV files are allowed
            </p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item
          name={"description"}
          label="Description"
          rules={[{ required: true, message: "Please Enter Description" }]}
        >
          <Input.TextArea autoSize={{ minRows: 4 }} showCount={false} />
        </Form.Item>
        <Form.Item>
          <Button
            htmlType="submit"
            type="primary"
            loading={loading}
          >
            SEND
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default UploadInvoice;
